import { ctpTokens } from '@hinge-health/react-component-library';
import { Theme } from '@mui/material';
import {
  ConfigurationStatus,
  FileParsingBatchStatus,
  FileProcessingStatus,
} from '../domain/enums';
import { ProviderHealth } from '../types';

/** Any status the module renders as a chip, cell, or strip segment. */
export type ChipStatus =
  | FileProcessingStatus
  | FileParsingBatchStatus
  | ConfigurationStatus
  | ProviderHealth;

/**
 * Semantic colour family for a status. Mirrors the v4 design spec (§3):
 * `ok` green, `bad` red, `warn` yellow, `info` sand, `mute` neutral.
 */
export type StatusTone = 'ok' | 'bad' | 'warn' | 'info' | 'mute';

/**
 * Indicator glyph drawn next to the label so status is never conveyed by
 * colour alone (WCAG 1.4.1). `filled` = terminal, `hollow` = waiting,
 * `half` = partial outcome, `pulse` = actively processing.
 */
export type StatusShape = 'filled' | 'hollow' | 'half' | 'pulse';

export interface StatusStyle {
  tone: StatusTone;
  shape: StatusShape;
}

/**
 * Single source of truth for how each wire status reads. Statuses shared by
 * the file and batch lifecycles (`PENDING`, `COMPLETED`, `FAILED`) resolve
 * to one entry so the same word never renders two different ways.
 */
const STATUS_STYLE_MAP: Record<ChipStatus, StatusStyle> = {
  // file processing lifecycle
  [FileProcessingStatus.Pending]: { tone: 'info', shape: 'hollow' },
  [FileProcessingStatus.Parsing]: { tone: 'warn', shape: 'pulse' },
  [FileProcessingStatus.Validating]: { tone: 'warn', shape: 'pulse' },
  [FileProcessingStatus.Resolving]: { tone: 'warn', shape: 'pulse' },
  [FileProcessingStatus.Finalizing]: { tone: 'warn', shape: 'pulse' },
  [FileProcessingStatus.Paused]: { tone: 'warn', shape: 'hollow' },
  [FileProcessingStatus.Cancelled]: { tone: 'mute', shape: 'filled' },
  [FileProcessingStatus.Completed]: { tone: 'ok', shape: 'filled' },
  [FileProcessingStatus.CompletedWithErrors]: { tone: 'warn', shape: 'half' },
  [FileProcessingStatus.Failed]: { tone: 'bad', shape: 'filled' },
  [FileProcessingStatus.Rejected]: { tone: 'bad', shape: 'filled' },
  // batch-only statuses
  [FileParsingBatchStatus.Processing]: { tone: 'warn', shape: 'pulse' },
  [FileParsingBatchStatus.Skipped]: { tone: 'mute', shape: 'hollow' },
  // configuration lifecycle
  [ConfigurationStatus.Draft]: { tone: 'mute', shape: 'hollow' },
  [ConfigurationStatus.Published]: { tone: 'ok', shape: 'filled' },
  [ConfigurationStatus.Deleted]: { tone: 'bad', shape: 'filled' },
  // provider health (overview)
  failing: { tone: 'bad', shape: 'filled' },
  inactive: { tone: 'mute', shape: 'hollow' },
  current: { tone: 'ok', shape: 'filled' },
};

/** Neutral, non-animated style for a status this build has no entry for. */
export const UNKNOWN_STATUS_STYLE: StatusStyle = {
  tone: 'mute',
  shape: 'hollow',
};

/**
 * Falls back to `UNKNOWN_STATUS_STYLE` rather than returning `undefined`: a
 * missing entry must degrade one chip, not throw and unmount the page
 * (ROI-2301).
 */
export const getStatusStyle = (status: ChipStatus): StatusStyle =>
  (STATUS_STYLE_MAP[status] as StatusStyle | undefined) ?? UNKNOWN_STATUS_STYLE;

export interface ToneColors {
  /** Text and indicator colour — the AA-contrast value for the tone. */
  fg: string;
  /** Surface tint behind the text. */
  bg: string;
}

/**
 * Resolves a tone to concrete colours from the active theme.
 *
 * Foreground values come from the theme's semantic slots so they follow a
 * theme swap. The 90-stop tints have no semantic slot in `defaultCtpTheme`,
 * so those are read from `ctpTokens` directly — the only place in the module
 * that touches raw tokens.
 */
export const getToneColors = (theme: Theme, tone: StatusTone): ToneColors => {
  const { palette } = theme;
  const tones: Record<StatusTone, ToneColors> = {
    ok: {
      fg: palette.text.success ?? palette.success.dark,
      bg: ctpTokens.palette.green[90],
    },
    bad: { fg: palette.error.dark, bg: ctpTokens.palette.red[90] },
    warn: {
      fg: palette.text.warning ?? palette.warning.dark,
      bg: ctpTokens.palette.yellow[90],
    },
    info: { fg: palette.text.primary, bg: palette.info.light },
    mute: { fg: palette.text.secondary, bg: ctpTokens.palette.neutral[95] },
  };

  return tones[tone];
};

/**
 * Whether a status indicator should animate. Split out from the `sx`
 * ternary so the reduced-motion gate is unit-testable alone. The animation
 * itself is capped under 5 s in `status-chip.tsx` (WCAG 2.2.2).
 */
export const shouldPulseStatus = (
  status: ChipStatus,
  prefersReducedMotion: boolean,
): boolean => getStatusStyle(status).shape === 'pulse' && !prefersReducedMotion;
